import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
// @mui
import { alpha } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// api
import {
  updateColumn,
  clearColumn, createTask,
  updateTask
} from 'src/api/kanban';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
//
import KanbanTaskAdd from './kanban-task-add';
import KanbanTaskItem from './kanban-task-item';
import KanbanColumnToolBar from './kanban-column-tool-bar';
import { deleteRequestmsg } from 'src/services';
import { endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------

export default function KanbanColumn({ column, tasks, index, reload }) {
  const { enqueueSnackbar } = useSnackbar();
  const openAddTask = useBoolean();

  const handleUpdateColumn = useCallback(
    async (columnName) => {
      try {
        if (column?.name !== columnName) {
          updateColumn(column, columnName);
          enqueueSnackbar('Update success!', {
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [ column, column?.name, enqueueSnackbar ]
  );

  const handleClearColumn = useCallback(async () => {
    try {
      clearColumn(column);
    } catch (error) {
      console.error(error);
    }
  }, [ column ]);

  const handleDeleteColumn = useCallback(async () => {
    try {
      const columnIdNumber = column.id.match(/\d+$/)?.[ 0 ];
      const response = await deleteRequestmsg(`${endpoints.admin_dashboard.marketing_management.board.crud_stage}/${columnIdNumber}`);
      reload();
      console.log("🚀 ~ handleDeleteColumn ~ response:", response)
    } catch (error) {
      console.error(error);
    }
  }, [ column, enqueueSnackbar ]);

  const handleAddTask = useCallback(
    async (taskData) => {
      try {
        createTask(column, taskData);
        openAddTask.onFalse();
      } catch (error) {
        console.error(error);
      }
    },
    [ column, openAddTask ]
  );
  const handleUpdateTask = useCallback(async (formData, taskId) => {
    try {
      await updateTask(taskId, formData);

    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to update task', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
  }, [ enqueueSnackbar ]);



  const handleDeleteTask = useCallback(async (taskId) => {
    try {
      const relatedtaskId = taskId.match(/\d+$/)?.[ 0 ];

      console.log("🚀 ~ handleDeleteTask ~ taskId:", relatedtaskId)
      await deleteRequestmsg(
        `${endpoints.admin_dashboard.marketing_management.board.delete_card}/${relatedtaskId}`
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to delete column', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
  }, [ column, enqueueSnackbar, reload ]);

  const renderAddTask = (
    <Stack spacing={ 2 } sx={ { pb: 3 } }>
      { openAddTask.value && (
        <KanbanTaskAdd
          status={ column.name }
          onAddTask={ handleAddTask }
          onCloseAddTask={ openAddTask.onFalse }
          columnId={ column.id }
          reload={ reload }

        />
      ) }
      <Button
        fullWidth
        size="large"
        color="inherit"
        startIcon={
          <Iconify
            icon={
              openAddTask.value
                ? 'solar:close-circle-broken'
                : 'mingcute:add-line'
            }
            width={ 18 }
            sx={ { mr: -0.5 } }
          />
        }
        onClick={ openAddTask.onToggle }
        sx={ { fontSize: 14 } }
      >
        { openAddTask.value ? 'Close' : 'Add Task' }
      </Button>
    </Stack>
  );

  return (
    <Draggable draggableId={ column.id } index={ index } >
      { (provided, snapshot) => (
        <Paper
          ref={ provided.innerRef }
          { ...provided.draggableProps }
          sx={ {
            px: 2,
            borderRadius: 2,
            boxShadow: " rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(29, 27, 27, 0.3) 0px 3px 7px -3px",
            bgcolor: 'background.neutral',
            ...(snapshot.isDragging && {
              bgcolor: (theme) => alpha(theme.palette.grey[ 500 ], 0.24),
            }),
          } }
        >
          <Stack { ...provided.dragHandleProps }>
            <KanbanColumnToolBar
              columnId={ column.id.split('-')[ 1 ] }
              columnName={ column?.name }
              onUpdateColumn={ handleUpdateColumn }
              onClearColumn={ handleClearColumn }
              onDeleteColumn={ handleDeleteColumn }
              reload={ reload }
            />

            <Droppable droppableId={ column.id } type="TASK">
              { (dropProvided) => (
                <Stack
                  ref={ dropProvided.innerRef }
                  { ...dropProvided.droppableProps }
                  spacing={ 2 }
                  sx={ { py: 3, width: 280 } }
                >
                  { column?.taskIds?.map((taskId, taskIndex) => (
                    <KanbanTaskItem
                      key={ taskId }
                      index={ taskIndex }
                      task={ tasks[ taskId ] }
                      onUpdateTask={ handleUpdateTask }
                      onDeleteTask={ () => handleDeleteTask(taskId) }
                      reload={ reload }
                    />
                  )) }
                  { dropProvided.placeholder }
                </Stack>
              ) }
            </Droppable>
            { renderAddTask }
          </Stack>
        </Paper>
      ) }
    </Draggable>
  );
}
KanbanColumn.propTypes = {
  column: PropTypes.object,
  index: PropTypes.number,
  tasks: PropTypes.object,
};